import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { debounce } from 'lodash-es';
import { useAppSelector } from './useAppSelector';
import { fetchSuburbSuggestionsFromRex } from 'Api/Rex/rexApi';
import { fetchSuburbSuggestions as fetchRpDataSuburbSuggestions } from 'Api/RpData/rpDataApi';
import { AustralianState, AustralianStateNumericMap } from '@urbanx/agx-ui-components';
import type { SuburbSuggestions } from '@urbanx/agx-ui-components';
import { useGoogleMapsPlacesSearch } from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';

const useSuburbSuggestions = (stateToSearch?: string) => {
  const [loading, setLoading] = useState(false);
  const [suburbSuggestions, setSuburbSuggestions] = useState<SuburbSuggestions[]>([]);
  const activeRequest = useRef<AbortController | undefined>(undefined);
  const { getAuthToken } = useContext(AuthContext);
  const selectedForm = useAppSelector((state) => state.form.selectedForm);
  const targetState = stateToSearch ?? selectedForm?.state;
  const { searchForAddresses, getDetailsForPlaceSuggestions } = useGoogleMapsPlacesSearch();

  // useCallback is intentional: the debounce depends on this function's identity staying stable,
  // including when React Compiler skips optimization of this hook.
  const fetchSuggestions = useCallback(
    async (searchQuery?: string) => {
      if (!searchQuery || searchQuery.length < 3) {
        setSuburbSuggestions([]);
        return;
      }

      if (!targetState) return;

      setLoading(true);

      const controller = new AbortController();
      activeRequest.current?.abort();
      activeRequest.current = controller;

      try {
        const authToken = await getAuthToken();
        if (!authToken || controller.signal.aborted) return;

        try {
          const result = await fetchSuburbSuggestionsFromRex(
            authToken,
            searchQuery,
            targetState,
            controller.signal
          );

          if (controller.signal.aborted) return;

          if (result && result.length > 0) {
            setSuburbSuggestions(
              result.map((suggestion) => ({
                state: suggestion.stateOrRegion as AustralianState,
                postcode: suggestion.postCode,
                suggestionLine1: suggestion.suburbOrTown,
                suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
              }))
            );
          }
        } catch (rexError) {
          if (controller.signal.aborted) return;

          console.log('Rex API failed, falling back to RPData API:', rexError);

          try {
            // If REX API fails, falling back to I (RPData API)
            const result = await fetchRpDataSuburbSuggestions(
              authToken,
              searchQuery,
              targetState,
              controller.signal
            );

            if (controller.signal.aborted) return;

            if (result?.suburbSuggestions) {
              setSuburbSuggestions(
                result.suburbSuggestions.map((suggestion) => ({
                  state:
                    AustralianState[
                      AustralianStateNumericMap[suggestion.state] as keyof typeof AustralianState
                    ],
                  postcode: suggestion.postcode,
                  suggestionLine1: suggestion.suggestionLine1,
                  suggestionLine2: suggestion.suggestionLine2,
                }))
              );
            }
          } catch (rpDataerror) {
            if (controller.signal.aborted) return;

            console.log('RPData API failed, falling back to Google Places API:', rpDataerror);

            try {
              // If both (REX & RPData APIs) fail, falling back to  (Google Places API)
              const predictions = await searchForAddresses(`${searchQuery}, ${targetState}`);
              const places = (await getDetailsForPlaceSuggestions(predictions)).filter(
                (place) => place !== undefined
              );

              if (controller.signal.aborted) return;

              if (places.length > 0) {
                setSuburbSuggestions(
                  places.flatMap((place) =>
                    place.postCode && place.state === targetState
                      ? [
                          {
                            state: place.state as AustralianState,
                            postcode: place.postCode,
                            suggestionLine1: place.suburb,
                            suggestionLine2: `${place.state} ${place.postCode}`,
                          },
                        ]
                      : []
                  )
                );
              }
            } catch (googlePlacesApiError) {
              console.log('Google Places API failed', googlePlacesApiError);
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setSuburbSuggestions([]);
      } finally {
        if (activeRequest.current === controller) setLoading(false);
      }
    },
    [getAuthToken, targetState, searchForAddresses, getDetailsForPlaceSuggestions]
  );
  // useMemo preserves the pending debounce timer across renders, including when compilation is skipped.
  const debouncedFetch = useMemo(() => debounce(fetchSuggestions, 300), [fetchSuggestions]);
  const getSuburbSuggestions = useCallback(
    (searchQuery?: string) => {
      activeRequest.current?.abort();
      activeRequest.current = undefined;
      debouncedFetch.cancel();
      setSuburbSuggestions([]);
      setLoading(false);
      const query = searchQuery?.trim();
      if (!query || query.length < 3) return;
      debouncedFetch(query);
    },
    [debouncedFetch]
  );

  useEffect(
    () => () => {
      debouncedFetch.cancel();
      activeRequest.current?.abort();
      activeRequest.current = undefined;
      setLoading(false);
    },
    [debouncedFetch]
  );

  return { suburbSuggestions, loading, getSuburbSuggestions };
};

export default useSuburbSuggestions;
